import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

interface DisplaySchedule {
  start_date: string;
  end_date: string;
  days: string[];
  hours: {
    start: string;
    end: string;
  };
}

interface ScheduleSectionProps {
  schedule: DisplaySchedule | null;
  onScheduleChange: (schedule: DisplaySchedule) => void;
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  schedule,
  onScheduleChange,
}) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const updateSchedule = (updates: Partial<DisplaySchedule>) => {
    const newSchedule = {
      start_date: schedule?.start_date || '',
      end_date: schedule?.end_date || '',
      days: schedule?.days || [],
      hours: schedule?.hours || { start: '18:00', end: '22:00' },
      ...updates,
    };
    onScheduleChange(newSchedule);
  };

  const handleDayToggle = (day: string, checked: boolean) => {
    const currentDays = schedule?.days || [];
    const newDays = checked
      ? [...currentDays, day]
      : currentDays.filter(d => d !== day);
    updateSchedule({ days: newDays });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule & Hours</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_date">Start Date</Label>
            <Input
              type="date"
              id="start_date"
              value={schedule?.start_date || ''}
              onChange={(e) => updateSchedule({ start_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="end_date">End Date</Label>
            <Input
              type="date"
              id="end_date"
              value={schedule?.end_date || ''}
              onChange={(e) => updateSchedule({ end_date: e.target.value })}
            />
          </div>
        </div>

        <div>
          <Label>Days of Operation</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {daysOfWeek.map((day) => (
              <div key={day} className="flex items-center space-x-2">
                <Checkbox
                  id={`day-${day}`}
                  checked={schedule?.days?.includes(day) || false}
                  onCheckedChange={(checked) => handleDayToggle(day, checked as boolean)}
                />
                <Label htmlFor={`day-${day}`} className="text-sm font-normal">
                  {day}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_time">Start Time</Label>
            <Input
              type="time"
              id="start_time"
              value={schedule?.hours?.start || '18:00'}
              onChange={(e) => updateSchedule({
                hours: {
                  ...schedule?.hours,
                  start: e.target.value,
                  end: schedule?.hours?.end || '22:00'
                }
              })}
            />
          </div>
          <div>
            <Label htmlFor="end_time">End Time</Label>
            <Input
              type="time"
              id="end_time"
              value={schedule?.hours?.end || '22:00'}
              onChange={(e) => updateSchedule({
                hours: {
                  start: schedule?.hours?.start || '18:00',
                  end: e.target.value
                }
              })}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Set when your display will be active during the season
        </p>
      </CardContent>
    </Card>
  );
};

export default ScheduleSection;